import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Printer, Send, ArrowLeft } from "lucide-react";
import { useAuth } from "../../context/Auth";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { useGlobalContext } from "../../context/stateContext";
import { showApiErrors } from "../../utils/helpers";

const DispatchShipment = () => {
  const { requisition_id } = useParams();
  const navigate = useNavigate();
  const { requestWithAuth } = useAuth();
  const { activeSession } = useGlobalContext();
  const printRef = useRef();
  const staffName = `${activeSession?.user?.first_name} ${activeSession?.user?.last_name}`;
  const [shipmentData, setShipmentData] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);
  const [branchName, setBranchName] = useState("");
  const [dispatchRiderName, setDispatchRiderName] = useState("");
  const [dispatchRiderPhone, setDispatchRiderPhone] = useState("");

  const [loading, setLoading] = useState(false);
  const [shipmentId, setShipmentId] = useState("");
  const [currentDate, setCurrentDate] = useState("");

  useEffect(() => {
    // Get data from sessionStorage
    const storedData = sessionStorage.getItem("dispatchData");
    if (storedData) {
      const data = JSON.parse(storedData);
      setShipmentData(data.shipmentData);
      setSelectedItems(data.selectedItems);
      setBranchName(data.branchName);
    } else {
      toast.error("No dispatch data found");
      navigate(`/authorization/requisition/detail/${requisition_id}`);
    }

    // Generate shipment ID and set current date
    const now = new Date();
    setShipmentId(
      `SH-${now.getFullYear()}${String(now.getMonth() + 1).padStart(
        2,
        "0"
      )}${String(now.getDate()).padStart(2, "0")}-${Math.floor(
        Math.random() * 10000
      )}`
    );
    setCurrentDate(
      now.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    );
  }, [requisition_id, navigate]);

  const handlePrint = () => {
    window.print();
  };

  const handleConfirmDispatch = async () => {
    if (!dispatchRiderName || !dispatchRiderPhone || !staffName) {
      toast.warning("Please fill in all required fields");
      return;
    }

    Swal.fire({
      icon: "question",
      title: "Confirm Dispatch",
      text: "Are you sure you want to confirm this dispatch?",
      showDenyButton: true,
      confirmButtonText: "Yes, Confirm!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          setLoading(true);

          // Make API call to confirm dispatch
          const dispatchPayload = {
            items: selectedItems.map((item) => item.id),
            dispatch_rider_name: dispatchRiderName,
            dispatch_rider_phone_number: dispatchRiderPhone,
            prepared_by: staffName,
            shipment_id: shipmentId,
            destination_branch: shipmentData.branch_id,
          };


          // API call here - uncomment when endpoint is ready
          await requestWithAuth(
            "/inventory/requisition/dispatch-items/",
            "POST",
            dispatchPayload
          );

          Swal.fire({
            icon: "success",
            title: "Dispatched!",
            text: "Items have been dispatched successfully",
          }).then(() => {
            sessionStorage.removeItem("dispatchData");
            navigate(-2);
          });
        } catch (error) {
          toast.error("Failed to dispatch items");
          showApiErrors(error);
          console.error(error);
        } finally {
          setLoading(false);
        }
      }
    });
  };

  if (!shipmentData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <>
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          .print-area, .print-area * {
            visibility: visible;
          }
          .print-area {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
          .no-print {
            display: none !important;
          }
          @page {
            margin: 1cm;
          }
        }
      `}</style>

      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-5xl mx-auto">
          {/* Header - Don't print this */}
          <div className="mb-8 no-print">
            <Link
              to={`/authorization/requisition/detail/${requisition_id}`}
              className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
            >
              <ArrowLeft size={20} />
              Back to Requisition
            </Link>
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
                  <span>Authorization</span>
                  <span>/</span>
                  <span>Requisition</span>
                  <span>/</span>
                  <span className="text-gray-900 font-medium">Dispatch</span>
                </div>
                <h1 className="text-3xl font-bold text-gray-900">
                  Dispatch Shipment
                </h1>
              </div>
            </div>
          </div>

          {/* Dispatch Form - Don't print this */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6 no-print">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              Dispatch Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Prepared By <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={staffName}
                  disabled
                  placeholder="Enter your name"
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Dispatch Rider Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={dispatchRiderName}
                  onChange={(e) => setDispatchRiderName(e.target.value)}
                  placeholder="Enter rider name"
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Dispatch Rider Phone <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  value={dispatchRiderPhone}
                  onChange={(e) => setDispatchRiderPhone(e.target.value)}
                  placeholder="Enter phone number"
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            <div className="mt-6 flex gap-3">
              <button
                onClick={handlePrint}
                className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2.5 rounded-lg font-medium transition-colors shadow-sm flex items-center gap-2"
              >
                <Printer size={20} />
                Print Packing List
              </button>
              <button
                onClick={handleConfirmDispatch}
                disabled={
                  loading ||
                  !dispatchRiderName ||
                  !dispatchRiderPhone ||
                  !staffName
                }
                className="bg-green-500 hover:bg-green-600 text-white px-6 py-2.5 rounded-lg font-medium transition-colors shadow-sm flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Confirming...
                  </>
                ) : (
                  <>
                    <Send size={20} />
                    Confirm Dispatch
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Printable Packing List */}
          <div
            ref={printRef}
            className="print-area bg-white rounded-xl shadow-sm border border-gray-200 p-8"
          >
            {/* Header */}
            <div className="border-b-2 border-gray-800 pb-4 mb-6">
              <h1 className="text-3xl font-bold text-gray-900 text-center mb-2">
                PACKING LIST
              </h1>
              <div className="text-center text-sm text-gray-600">
                <p className="font-semibold">Life Chemist Ltd.</p>
              </div>
            </div>

            {/* Shipment Details */}
            <div className="grid grid-cols-2 gap-6 mb-6">
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-2 uppercase">
                  Shipment Information
                </h3>
                <div className="space-y-1 text-sm">
                  <p>
                    <span className="font-medium">Shipment ID:</span>{" "}
                    {shipmentId}
                  </p>
                  <p>
                    <span className="font-medium">Date:</span> {currentDate}
                  </p>
                </div>
              </div>

              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-2 uppercase">
                  From
                </h3>
                <div className="space-y-1 text-sm">
                  <p className="font-medium">{activeSession?.branch?.name}</p>
                </div>
              </div>
            </div>

            <div className="mb-6">
              <h3 className="text-sm font-semibold text-gray-900 mb-2 uppercase">
                To
              </h3>
              <div className="space-y-1 text-sm">
                <p className="font-medium">{branchName}</p>
              </div>
            </div>

            {/* Items Table */}
            <div className="mb-8">
              <h3 className="text-sm font-semibold text-gray-900 mb-3 uppercase">
                Items Details
              </h3>
              <table className="w-full border-collapse border border-gray-300">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border border-gray-300 px-3 py-2 text-left text-xs font-semibold">
                      S/N
                    </th>
                    <th className="border border-gray-300 px-3 py-2 text-left text-xs font-semibold">
                      Drug Name
                    </th>
                    <th className="border border-gray-300 px-3 py-2 text-center text-xs font-semibold">
                      Requested Qty
                    </th>
                    <th className="border border-gray-300 px-3 py-2 text-center text-xs font-semibold">
                      Supplied Qty
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {selectedItems.map((item, index) => (
                    <tr key={item.id}>
                      <td className="border border-gray-300 px-3 py-2 text-sm">
                        {index + 1}
                      </td>
                      <td className="border border-gray-300 px-3 py-2 text-sm font-medium">
                        {item.drug}
                      </td>
                      <td className="border border-gray-300 px-3 py-2 text-center text-sm">
                        {item.quantity}
                      </td>
                      <td className="border border-gray-300 px-3 py-2 text-center text-sm font-semibold">
                        {item.quantity_supplied}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Summary */}
            <div className="mb-8 p-4 bg-gray-50 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="font-semibold text-gray-900">
                  Total Items Supplied:
                </span>
                <span className="text-2xl font-bold text-gray-900">
                  {selectedItems.reduce(
                    (total, item) =>
                      parseFloat(total) + parseFloat(item.quantity_supplied),
                    0
                  )}
                </span>
              </div>
            </div>

            {/* Footer Information */}
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <p className="font-medium text-gray-900">Prepared By:</p>
                  <p className="text-gray-700">
                    {staffName || "________________________"}
                  </p>
                </div>
                <div>
                  <p className="font-medium text-gray-900">Dispatch Rider:</p>
                  <p className="text-gray-700">
                    {dispatchRiderName || "________________________"}
                  </p>
                  <p className="text-gray-600">
                    {dispatchRiderPhone || "Phone: _______________"}
                  </p>
                </div>
              </div>

              <div className="border-t border-gray-300 pt-6 mt-8">
                <h3 className="font-semibold text-gray-900 mb-4 uppercase">
                  Signatures
                </h3>
                <div className="grid grid-cols-1 gap-6">
                  <div className="flex justify-between items-end border-b border-gray-300 pb-2">
                    <span className="text-gray-700">
                      Prepared by: &nbsp; {staffName}
                    </span>
                    <div className="flex gap-8 items-end">
                      <div>
                        <span className="text-gray-600">
                          Signature: _________________
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-600">
                          Date: _____________
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-between items-end border-b border-gray-300 pb-2">
                    <span className="text-gray-700">
                      Received by Rider: &nbsp; {dispatchRiderName}
                    </span>
                    <div className="flex gap-8 items-end">
                      <div>
                        <span className="text-gray-600">
                          Signature: _________________
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-600">
                          Date: _____________
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-between items-end border-b border-gray-300 pb-2">
                    <span className="text-gray-700">Received at Branch:</span>
                    <div className="flex gap-8 items-end">
                      <div>
                        <span className="text-gray-600">
                          Signature: _________________
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-600">
                          Date: _____________
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-8 text-center text-xs text-gray-500 border-t border-gray-200 pt-4">
                <p>
                  This is a computer-generated document. Please verify all items
                  upon receipt.
                </p>
                <p className="mt-1">
                  For any discrepancies, contact Head Office immediately.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default DispatchShipment;
